import { Tooltip } from '@/components/ui/tooltip';
import type { StackProps } from '@chakra-ui/react';
import {
  FLAME_SLOTS,
  isPositiveMatchTier,
  litFlameCount,
  MatchFlames as MatchFlamesMark,
  type MatchFlamesSize as SharedMatchFlamesSize,
} from '@fluenceur/agent-blocks';
import type { MatchTier } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { matchScoreToTier } from './match-score-tier';

/**
 * The mark itself (three flame slots, fills, sizes, skeleton) lives in
 * `@fluenceur/agent-blocks`, shared with the companion panel and the MCP
 * widgets. This file only adds what the dashboard owns: the translated tier
 * name and the hover tooltip.
 */
export { MatchFlamesSkeleton } from '@fluenceur/agent-blocks';

export type MatchFlamesSize = Exclude<SharedMatchFlamesSize, 'panel'>;

interface MatchFlamesProps extends Omit<StackProps, 'children'> {
  /**
   * Preferred prop. Comes straight from the backend brief signal vector on
   * the recommendations surface.
   */
  tier?: MatchTier;
  /**
   * Fallback for the surfaces that only hold a cosine-derived 0-100 score
   * (Discovery search, mention opportunity signal). Ignored when `tier` is set.
   */
  score?: number | null;
  size?: MatchFlamesSize;
}

/**
 * Fit indicator for creator surfaces: a three-slot flame scale replacing the
 * former coloured tier badge. The tier name stays reachable on hover and for
 * screen readers, never as list-level text.
 */
export const MatchFlames: React.FC<MatchFlamesProps> = ({
  tier,
  score,
  size = 'sm',
  ...stackProps
}) => {
  const { t } = useTranslation();

  const resolvedTier = tier ?? matchScoreToTier(score);
  if (!isPositiveMatchTier(resolvedTier)) return null;

  const label = t(`recommendations.matchScore.tier.${resolvedTier}.label`);
  const tooltip = t(`recommendations.matchScore.tier.${resolvedTier}.tooltip`);

  return (
    <Tooltip content={tooltip}>
      <MatchFlamesMark
        tier={resolvedTier}
        size={size}
        ariaLabel={t('recommendations.matchScore.flames.ariaLabel', {
          label,
          count: litFlameCount[resolvedTier],
          total: FLAME_SLOTS,
        })}
        {...stackProps}
      />
    </Tooltip>
  );
};
